import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Mail, Phone, Save } from 'lucide-react';
import { settingsAPI } from '../utils/api';
import { toast } from 'sonner';

const Settings = () => {
  const [form, setForm] = useState({
    shopName: 'V Watch House',
    whatsappNumber: '',
    whatsappDisplay: '',
    inquiryTemplate: 'Hi, I\'m interested in the {brand} {model} (Ref: {ref}), listed at {price}. Is it still available?',
    responseTime: 'Usually within 2 hours',
    contactEmail: '',
    contactPhone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await settingsAPI.get();
        if (data) setForm(prev => ({ ...prev, ...data }));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(form);
      toast.success('Settings saved');
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const previewMessage = form.inquiryTemplate
    .replace('{brand}', 'Rolex').replace('{model}', 'Submariner')
    .replace('{ref}', '126610LN').replace('{price}', '$14,100')
    .replace('{category}', 'Dive').replace('{year}', '2024');

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground animate-pulse">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1 text-sm">Configure your shop and contact details</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(42,44%,62%)]">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Forms */}
        <div className="space-y-5">
          {/* Shop Identity */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Shop Identity</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Shop Name</Label>
                <Input value={form.shopName} onChange={e => setForm({ ...form, shopName: e.target.value })} placeholder="V Watch House" />
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp Concierge */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="h-4 w-4 text-green-500" /> WhatsApp Concierge</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp Number *</Label>
                <Input value={form.whatsappNumber} onChange={e => setForm({ ...form, whatsappNumber: e.target.value })} placeholder="+971501234567" />
                <p className="text-[10px] text-muted-foreground">International format with country code</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Display Name</Label>
                <Input value={form.whatsappDisplay} onChange={e => setForm({ ...form, whatsappDisplay: e.target.value })} placeholder="V Watch Concierge" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Inquiry Template</Label>
                <Textarea value={form.inquiryTemplate} onChange={e => setForm({ ...form, inquiryTemplate: e.target.value })} rows={3} className="text-sm font-mono" />
                <p className="text-[10px] text-muted-foreground">Variables: {'{brand}'} {'{model}'} {'{ref}'} {'{price}'} {'{category}'} {'{year}'}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Response Time</Label>
                <Input value={form.responseTime} onChange={e => setForm({ ...form, responseTime: e.target.value })} placeholder="Usually within 2 hours" />
              </div>
            </CardContent>
          </Card>

          {/* Fallback Contacts */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Fallback Contacts</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label>
                <Input type="email" value={form.contactEmail} onChange={e => setForm({ ...form, contactEmail: e.target.value })} placeholder="contact@vwatch.shop" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</Label>
                <Input value={form.contactPhone} onChange={e => setForm({ ...form, contactPhone: e.target.value })} placeholder="+971501234567" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Live Preview */}
        <div>
          <Card className="sticky top-20">
            <CardHeader className="pb-3"><CardTitle className="text-sm">WhatsApp Card Preview</CardTitle></CardHeader>
            <CardContent>
              <div className="rounded-xl border border-border overflow-hidden">
                {/* Green header */}
                <div className="bg-green-700 px-4 py-3">
                  <p className="text-white text-sm font-medium">{form.whatsappDisplay || 'V Watch Concierge'}</p>
                  <p className="text-green-200 text-xs">{form.whatsappNumber || '+971...'}</p>
                </div>
                {/* Message preview */}
                <div className="bg-[#1a1a1f] p-4">
                  <div className="bg-[#252530] rounded-lg p-3 max-w-[85%]">
                    <p className="text-sm text-[#e5e5e5] whitespace-pre-line">{previewMessage}</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">{form.responseTime}</p>
                </div>
                {/* CTA */}
                <div className="p-3 bg-[#141519] border-t border-border">
                  <a href={`https://wa.me/${(form.whatsappNumber || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(previewMessage)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="block bg-green-600 hover:bg-green-700 text-white text-center py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer">
                    Open WhatsApp
                  </a>
                </div>
              </div>
              {/* Fallback */}
              {(form.contactEmail || form.contactPhone) && (
                <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                  {form.contactEmail && <p className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {form.contactEmail}</p>}
                  {form.contactPhone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {form.contactPhone}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;
